import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { withTranslation } from "react-i18next"
import { useNavigate, useLocation } from "react-router-dom"
import {
  Card,
  CardBody,
  Container,
  Row,
  Col,
  Button,
  Form,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Label,
  Alert,
  UncontrolledTooltip,
  Collapse,
} from "reactstrap"
import {
  getVideoRoomSettings,
  searchAdminUserByName,
} from "helpers/backend_helper"
import {
  addUsersToGroup,
  createNewUser,
  createNewUserGroup,
  deleteUserGroup,
  getUserList,
  removeUsersFromGroup,
} from "helpers/keycloak_api_helper"
import KeycloakUserDrawer from "./KeycloakUserDrawer"
import {
  defaultPageSizeOptions,
  getLottieLoadingOptions,
  getLottieSearch,
  showToast,
  stringSorter,
  downloadExcel,
  checkboxFormatter,
  dateSorter,
  dateFormatter,
  booleanSorter,
} from "utils/general"
import SelectGroupFilters from "components/DropdownList/SelectGroupFilters"
import TagsTree from "components/DropdownList/TagsTree"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import Lottie from "react-lottie"
import jsPDF from "jspdf"
import "jspdf-autotable"
import {
  Table as AntdTable,
  Switch,
  Form as AntdForm,
  Input as AntdInput,
  Tooltip,
} from "antd"
import { useFormik } from "formik"
import * as Yup from "yup"
import classnames from "classnames"
import { tableDefaultHeight } from "constants/layout"
import moment from "moment"
const { Search } = AntdInput

const KeycloakUsers = props => {
  const [group, setGroup] = useState(null)
  const [users, setUsers] = useState([])
  const [filteredUsers, setFilteredUsers] = useState([])
  const [addModal, setAddModal] = useState(false)
  const [groupAddModal, setGroupAddModal] = useState(false)
  const [groupDeleteModal, setGroupDeleteModal] = useState(false)
  const [selectedRow, setSelectedRow] = useState(null)
  const [loading, setLoading] = useState(false)
  const [showDrawer, setShowDrawer] = useState(false)
  const [searchText, setSearchText] = useState("")
  const [ongoingAdd, setOngoingAdd] = useState(false)
  const [showLinked, setShowLinked] = useState(true)
  const [fetchUsersGroups, setFetchUsersGroups] = useState(false)
  const [updateCounter, setUpdateCounter] = useState(0)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [menu, setMenu] = useState(false) //Dropdown button state
  const [selectedTags, setSelectedTags] = useState([])

  const [userSearch, setUserSearch] = useState("")
  const [searchingUser, setSearchingUser] = useState(false)
  const [searchTriggers, setSearchTriggers] = useState(0) // Delayed search

  const navigate = useNavigate()
  const location = useLocation()

  const abortRef = useRef(null)
  useEffect(() => {
    document.title = props.t("User accounts") + " | Suvanto Care"
    abortRef.current = new AbortController()
    return () => abortRef.current.abort()
  }, [])

  // Set group to URL dynamically
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    if (group) {
      searchParams.set("group", group)
    } else {
      searchParams.delete("group")
    }
    navigate(`?${searchParams}`, { replace: true })
  }, [group, navigate])

  // Set group according to URL
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    setGroup(params.get("group") || null)
  }, [])

  useEffect(() => {
    //console.log("Group changed -> get users")
    if (group) getAdmins()
  }, [group, showLinked, fetchUsersGroups])

  useEffect(() => {
    //console.log("Group: ", group, ", Loading: ", loading)
    if (showLinked) {
      setSelectedRowKeys([])
      // console.log(group, "groups")
    }
    if (!group || group.length == 0) {
      setFilteredUsers([])
    }
  }, [group])

  // Search or filter changed
  useEffect(() => {
    handleSearch()
  }, [searchText, users])

  useEffect(() => {
    searchUser()
  }, [searchTriggers])

  // Handle search function
  const handleSearch = () => {
    let result = users.filter(
      data =>
        data.username?.toLowerCase().includes(searchText) ||
        data.description?.toLowerCase().includes(searchText) ||
        data.groups?.toLowerCase().includes(searchText.toLowerCase()) ||
        data.gsm?.toLowerCase().includes(searchText) ||
        data.email?.toLowerCase().includes(searchText) ||
        data.fullName?.toLowerCase().includes(searchText)
    )
    // console.log(users, result, searchText, "filtered : resultFiltered")
    setFilteredUsers(result)
  }

  // Get admin users for table
  const getAdmins = () => {
    setLoading(true)
    setUsers([])
    getUserList(abortRef.current, group, !showLinked, fetchUsersGroups)
      .then(result => {
        if (Array.isArray(result)) {
          result.sort((a, b) => (a.username > b.username ? 1 : -1))
          for (const user of result) {
            let first = user.firstName ?? ""
            let last = user.lastName ?? ""
            user.fullName = (first + " " + last).trim()
            if (user.attributes) {
              user.description = user.attributes.description
                ? user.attributes.description[0]
                : ""
              user.gsm = user.attributes.gsm ? user.attributes.gsm[0] : ""
              user.videoRoomId = user.attributes.videoRoomId
                ? user.attributes.videoRoomId[0]
                : ""
              user.lastLogin = user.attributes.lastLogin
                ? moment(user.attributes.lastLogin[0]).valueOf()
                : 0
            }
          }

          // Get video room settings
          const videoRoomIds = result
            .map(user => user.attributes?.videoRoomId?.[0])
            .filter(id => id) // Remove undefined/null values

          // Combine values
          ;(async () => {
            let groupHostValues = await getRooms(videoRoomIds)
            const userValues = result.map(room => ({
              ...room,
              groupHost:
                groupHostValues.find(
                  entry => entry.roomName === room.videoRoomId
                )?.groupCallHost || null,
            }))
            setUsers(userValues)
          })()
        } else {
          setUsers([])
        }
      })
      .catch(err => {
        console.log(err)
        setUsers([])
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const getRooms = async videoRoomIds => {
    try {
      const result = await getVideoRoomSettings(
        abortRef.current,
        JSON.stringify(videoRoomIds)
      )
      return result.entries
    } catch (err) {
      console.log(err)
      return []
    }
  }

  // Link admins to a group
  const linkAdmins = () => {
    if (!loading) {
      setLoading(true)
      addUsersToGroup(abortRef.current, group, selectedRowKeys)
        .then(result => {
          if (result == "OK") {
            getAdmins()
            showToast(props.t("Success"), "success", 3000)
          } else {
            showToast(props.t("Error"), "error")
          }
        })
        .catch(err => {
          console.log(err)
          showToast(props.t("Error"), "error")
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  // Unlink admins from a group
  const unlinkAdmins = () => {
    if (!loading) {
      setLoading(true)
      //console.log("Unlink keys: ", selectedRowKeys)
      removeUsersFromGroup(abortRef.current, group, selectedRowKeys)
        .then(result => {
          if (result == "OK") {
            getAdmins()
            showToast(props.t("Success"), "success", 3000)
          } else {
            showToast(props.t("Error"), "error")
          }
        })
        .catch(err => {
          console.log(err)
          showToast(props.t("Error"), "error")
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  // Search user by username
  const searchUser = () => {
    if (!searchingUser && userSearch) {
      setSearchingUser(true)
      searchAdminUserByName(abortRef.current, userSearch)
        .then(result => {
          if (result.array?.length == 1) {
            setSelectedRow(result.array[0])
            setShowDrawer(true)
          } else {
            showToast(props.t("User not found"), "info", 2000)
          }
        })
        .catch(err => {
          console.log(err)
          showToast(props.t("Error"), "error", 2000)
        })
        .finally(() => {
          setSearchingUser(false)
        })
    }
  }

  // Reset selections
  const resetSelections = () => {
    setSelectedRowKeys([])
  }

  // Create user validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      username: "",
    },
    validationSchema: Yup.object({
      username: Yup.string()
        .matches(
          /^[a-z0-9]+[a-z0-9-]*[a-z0-9]+[.][a-z0-9]+[a-z0-9-]*[a-z0-9]$/,
          props.t("Invalid format")
        )
        .trim()
        .min(7, props.t("Too short"))
        .required(props.t("Required field")),
    }),
    onSubmit: values => {
      console.log("Submit: ", values)
      if (values.username && group) {
        setOngoingAdd(true)
        createNewUser(abortRef.current, values.username.toLowerCase(), group)
          .then(result => {
            if (result == "OK") {
              showToast(props.t("Created"), "success")
              setAddModal(false)
              validation.resetForm()
              getAdmins()
            } else {
              showToast(props.t("Error"), "error")
            }
          })
          .catch(err => {
            console.log(err)
            showToast(props.t("Error"), "error")
          })
          .finally(() => {
            setOngoingAdd(false)
          })
      }
    },
  })

  // Create group validation
  const groupValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      group: "",
    },
    validationSchema: Yup.object({
      group: Yup.string()
        .matches(
          /^[A-ZÄÖÅ0-9]+[a-zäöåA-ZÄÖÅ0-9- ]*[a-zäöåA-ZÄÖÅ0-9]$/,
          props.t("Invalid format")
        )
        .trim()
        .min(4, props.t("Too short"))
        .required(props.t("Required field")),
    }),
    onSubmit: values => {
      console.log("Submit: ", values)
      setOngoingAdd(true)

      createNewUserGroup(abortRef.current, values.group)
        .then(result => {
          if (result == "OK") {
            showToast(props.t("Created"), "success")
            setGroupAddModal(false)
            groupValidation.resetForm()
            setUpdateCounter(prevCount => prevCount + 1)
          } else {
            showToast(props.t("Error"), "error")
          }
        })
        .catch(err => {
          console.log(err)
          showToast(props.t("Error"), "error")
        })
        .finally(() => {
          setOngoingAdd(false)
        })
    },
  })

  // Delete selected group
  const groupDelete = () => {
    if (group) {
      setOngoingAdd(true)

      deleteUserGroup(abortRef.current, group)
        .then(result => {
          if (result == "OK") {
            showToast(props.t("Deleted"), "success")
            setGroupDeleteModal(false)
            setGroup(null)
            setUpdateCounter(prevCount => prevCount + 1)
          } else {
            showToast(props.t("Error"), "error")
          }
        })
        .catch(err => {
          console.log(err)
          showToast(props.t("Error"), "error")
        })
        .finally(() => {
          setOngoingAdd(false)
        })
    }
  }

  // Table structure
  const columns = [
    {
      title: props.t("Actions"),
      dataIndex: "actions",
      align: "center",
      render: editFormatter,
      fixed: "left",
      width: "100px",
      ellipsis: true,
    },
    {
      title: props.t("Linked"),
      dataIndex: "linked",
      defaultSortOrder: "descend",
      sorter: (a, b) => a.linked - b.linked,
      align: "center",
      render: checkboxFormatter,
      width: "100px",
      ellipsis: true,
    },
    {
      title: props.t("Username"),
      dataIndex: "username",
      render: usernameFormatter,
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "username", sortOrder)
      },
      width: "200px",
      ellipsis: true,
    },
    {
      title: props.t("Email"),
      dataIndex: "email",
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "email", sortOrder)
      },
      render: emailFormatter,
      width: "250px",
      ellipsis: true,
    },
    ...(fetchUsersGroups
      ? [
          {
            title: props.t("Professional"),
            dataIndex: "basicUser",
            sorter: (a, b) => a.basicUser - b.basicUser,
            render: basicFormatter,
            align: "center",
            width: "150px",
            ellipsis: true,
          },
        ]
      : []),
    {
      title: props.t("Name"),
      dataIndex: "fullName",
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "fullName", sortOrder)
      },
      width: "200px",
      ellipsis: true,
    },
    {
      title: props.t("Description"),
      dataIndex: "description",
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "description", sortOrder)
      },
      width: "200px",
      ellipsis: true,
    },
    {
      title: props.t("GSM"),
      dataIndex: "gsm",
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "gsm", sortOrder)
      },
      width: "150px",
      ellipsis: true,
    },
    {
      title: props.t("Last login"),
      dataIndex: "lastLogin",
      sorter: (a, b, sortOrder) => {
        return dateSorter(a["lastLogin"], b["lastLogin"], sortOrder)
      },
      render: dateFormatter,
      width: "190px",
      ellipsis: true,
    },
    {
      title: props.t("Enabled"),
      dataIndex: "enabled",
      align: "center",
      render: cell => {
        return cell ? (
          <i className="mdi mdi-check-bold text-primary" />
        ) : (
          <i className="mdi mdi-close-thick text-danger" />
        )
      },
      sorter: (a, b, sortOrder) => {
        return booleanSorter(a, b, "enabled", sortOrder)
      },
      width: "100px",
      ellipsis: true,
    },
    ...(fetchUsersGroups
      ? [
          {
            title: props.t("Groups"),
            dataIndex: "groups",
            sorter: (a, b, sortOrder) => {
              return stringSorter(a, b, "groups", sortOrder)
            },
            width: "150px",
            ellipsis: true,
          },
        ]
      : []),
  ]

  function editFormatter(cell, row) {
    return (
      <>
        <Button
          color="primary"
          outline
          size="sm"
          onClick={() => {
            setShowDrawer(true)
            setSelectedRow(row)
          }}
        >
          <i className="mdi mdi-pencil" />
        </Button>
      </>
    )
  }

  function usernameFormatter(cell, row) {
    return (
      <>
        <span className={classnames({ "text-danger": !row.enabled })}>
          {row.username}
        </span>
        {!row.enabled && (
          <span className="ms-2 badge bg-danger font-size-12">
            {props.t("Disabled")}
          </span>
        )}
        {row.videoRoomId && !!row.groupHost && (
          <i className="ms-2 mdi mdi-account-voice text-primary" />
        )}
      </>
    )
  }

  function emailFormatter(cell, row) {
    return (
      <>
        {!row.emailVerified && !!cell && (
          <i className="me-1 mdi mdi-alert-circle text-danger" />
        )}
        <span>{cell}</span>
      </>
    )
  }

  function basicFormatter(cell, row) {
    if (row.basicUser) {
      return <i className="mdi mdi-check-bold text-primary"></i>
    } else return <>{/* <i className="mdi mdi-close text-light"></i> */}</>
  }

  const rowSelection = {
    selectedRowKeys,
    onSelect: (row, isSelect, rowArray) => {
      if (isSelect) {
        setSelectedRowKeys(prevState => [...prevState, row.id])
      } else {
        setSelectedRowKeys(prevState =>
          prevState.filter(prevItem => prevItem !== row.id)
        )
      }
    },
    onSelectAll: (isSelect, object, rowArray) => {
      if (isSelect) {
        rowArray.map(row =>
          setSelectedRowKeys(prevState => [...prevState, row.id])
        )
      } else {
        setSelectedRowKeys([])
      }
    },
  }

  // Export PDF table
  const exportPDF = () => {
    let message = props.t("PDF file exported succesfully!")
    const unit = "pt"
    const size = "A4" // Use A1, A2, A3 or A4
    const orientation = "landscape" // portrait or landscape
    const theme = "grid" //striped,  plain

    const marginLeft = 40
    const doc = new jsPDF(orientation, unit, size, theme)

    doc.setFontSize(14)

    let data = filteredUsers.map(elt => [
      elt.username,
      // elt.description,
      elt.fullName,
      elt.basicUser ? props.t("Yes") : props.t("No"),
      elt.groups,
      elt.email,
      elt.gsm,
      elt.enabled ? props.t("Yes") : props.t("No"),
      dateFormatter(elt.lastLogin),
    ])
    //console.log(data, "pdf function")

    let content = {
      startY: 50,
      head: [
        [
          props.t("Username"),
          //props.t("Description"),
          props.t("Name"),
          props.t("Professional"),
          props.t("Groups"),
          props.t("Email"),
          props.t("GSM"),
          props.t("Enabled"),
          props.t("Last login"),
        ],
      ],
      body: data,
      styles: { overflow: "linebreak" },
      columnStyles: {
        4: { cellWidth: 60 },
      },
    }

    // Different export if user groups not fetched
    if (!fetchUsersGroups) {
      data = filteredUsers.map(elt => [
        elt.username,
        //elt.description,
        elt.fullName,
        elt.email,
        elt.gsm,
        elt.enabled ? props.t("Yes") : props.t("No"),
        dateFormatter(elt.lastLogin),
      ])

      content = {
        startY: 50,
        head: [
          [
            props.t("Username"),
            //props.t("Description"),
            props.t("Name"),
            props.t("Email"),
            props.t("GSM"),
            props.t("Enabled"),
            props.t("Last login"),
          ],
        ],
        body: data,
      }
    }

    doc.text(group, marginLeft, 40)
    doc.autoTable(content)
    doc.save(group + " (" + new Date() + ")" + ".pdf")

    //Show toast
    showToast(message, "success")
  }

  // Creating Excel workbook+worksheet+download
  const generateExcel = () => {
    let heading = [
      [
        props.t("Username"),
        //props.t("Description"),
        props.t("Name"),
        props.t("Professional"),
        props.t("Groups"),
        props.t("Email"),
        props.t("GSM"),
        props.t("Enabled"),
        props.t("Last login"),
      ],
    ]
    let data = filteredUsers.map(elt => [
      elt.username,
      //elt.description,
      elt.fullName,
      elt.basicUser,
      elt.groups,
      elt.email,
      elt.gsm,
      elt.enabled,
      dateFormatter(elt.lastLogin),
    ])

    // Different export if user groups not fetched
    if (!fetchUsersGroups) {
      heading = [
        [
          props.t("Username"),
          //props.t("Description"),
          props.t("Name"),
          props.t("Email"),
          props.t("GSM"),
          props.t("Enabled"),
          props.t("Last login"),
        ],
      ]

      data = filteredUsers.map(elt => [
        elt.username,
        //elt.description,
        elt.fullName,
        elt.email,
        elt.gsm,
        elt.enabled,
        dateFormatter(elt.lastLogin),
      ])
    }

    downloadExcel(group, heading, data)
    showToast(props.t("Excel file exported succesfully!"), "success")
  }

  return (
    <>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title={props.t("User accounts")} />

          <Row>
            <Col className="mb-3" style={{ maxWidth: "280px" }}>
              <Search
                placeholder={props.t("Username")}
                allowClear
                value={userSearch}
                onChange={event => {
                  setUserSearch(event.target.value)
                }}
                onSearch={v => {
                  setSearchTriggers(prevValue => prevValue + 1)
                }}
                style={{ minWidth: "200px" }}
              />
            </Col>
            <Col
              className="mb-3"
              style={{ minWidth: "200px", maxWidth: "400px" }}
            >
              <div className="d-inline-block" style={{ width: "100%" }}>
                <TagsTree
                  loadData={true}
                  expanded={true}
                  selectionChanged={setSelectedTags}
                />
              </div>
            </Col>
            <Col
              className="mb-3"
              style={{ minWidth: "200px", maxWidth: "400px" }}
            >
              <SelectGroupFilters
                selection={group}
                onChange={setGroup}
                multiple={false}
                selectedTags={selectedTags}
                updateCounter={updateCounter}
              />
            </Col>
            <Col className="col-auto mb-3">
              <Button
                id="add-button"
                color="success"
                className="square-icon-button-sm"
                disabled={loading}
                onClick={() => {
                  setGroupAddModal(true)
                }}
              >
                <UncontrolledTooltip placement="bottom" target="add-button">
                  {props.t("Create a new group")}
                </UncontrolledTooltip>
                <i className="mdi mdi-account-multiple-plus font-size-18"></i>
              </Button>
              <Button
                id="delete-button"
                color="danger"
                className="square-icon-button-sm ms-3"
                disabled={!group || loading}
                onClick={() => {
                  setGroupDeleteModal(true)
                }}
              >
                <UncontrolledTooltip placement="bottom" target="delete-button">
                  {props.t("Delete group")}
                </UncontrolledTooltip>
                <i className="mdi mdi-trash-can font-size-18"></i>
              </Button>
            </Col>
          </Row>

          {!group && !loading && (
            <Card>
              <CardBody>
                <div className="text-center my-5">
                  <Lottie
                    options={getLottieSearch()}
                    height={150}
                    width={180}
                  ></Lottie>
                </div>
              </CardBody>
            </Card>
          )}

          {!!group && (
            <div>
              <Card>
                <CardBody>
                  {users != null && (
                    <React.Fragment>
                      <Row className="mb-2">
                        <Col className="mb-3" style={{ maxWidth: "320px" }}>
                          <Search
                            placeholder={props.t("Search")}
                            allowClear
                            value={searchText}
                            onChange={event => {
                              setSearchText(event.target.value)
                            }}
                            style={{ minWidth: "240px" }}
                          />
                        </Col>
                        <Col className="col-auto">
                          <Button
                            //style={{ borderRadius: "100px" }}
                            className="compact-button"
                            color="success"
                            disabled={loading}
                            onClick={() => {
                              setAddModal(true)
                            }}
                          >
                            <i className="fas fa-user-plus"></i>
                            <span className="d-none d-lg-inline-block ms-2">
                              {props.t("Create a new user")}
                            </span>
                          </Button>
                        </Col>
                        <Col className="mt-1 col-auto">
                          <Tooltip
                            title={props.t("Show only linked users")}
                            trigger={["hover", "focus"]}
                            placement="bottom"
                          >
                            <Switch
                              id="showLinkedSwitch"
                              name="showLinked"
                              checked={showLinked}
                              disabled={loading}
                              onChange={v => {
                                setShowLinked(v)
                              }}
                              checkedChildren={
                                <div
                                  className="d-flex"
                                  style={{ alignItems: "center" }}
                                >
                                  <i
                                    className="bx bx-link font-size-16"
                                    style={{ marginTop: "3px" }}
                                  />
                                </div>
                              }
                              unCheckedChildren={
                                <div
                                  className="d-flex"
                                  style={{ alignItems: "center" }}
                                >
                                  <i
                                    className="bx bx-link font-size-16"
                                    style={{ marginTop: "3px" }}
                                  />
                                </div>
                              }
                            />
                          </Tooltip>
                          <Tooltip
                            title={props.t("Fetch users groups")}
                            trigger={["hover", "focus"]}
                            placement="bottom"
                          >
                            <Switch
                              id="fetchUsersGroupsSwitch"
                              name="fetchUsersGroups"
                              className="ms-2"
                              checked={fetchUsersGroups}
                              disabled={loading}
                              onChange={v => {
                                setFetchUsersGroups(v)
                              }}
                              checkedChildren={
                                <div
                                  className="d-flex"
                                  style={{ alignItems: "center" }}
                                >
                                  <i
                                    className="bx bx-group font-size-18"
                                    style={{ marginTop: "2px" }}
                                  />
                                </div>
                              }
                              unCheckedChildren={
                                <div
                                  className="d-flex"
                                  style={{ alignItems: "center" }}
                                >
                                  <i
                                    className="bx bx-group font-size-18"
                                    style={{ marginTop: "2px" }}
                                  />
                                </div>
                              }
                            />
                          </Tooltip>
                        </Col>

                        <Col>
                          <span className="float-end ms-2">
                            <UncontrolledTooltip
                              placement="bottom"
                              target="downloadstatstable"
                            >
                              {props.t("Download statistics")}
                            </UncontrolledTooltip>
                            <Dropdown
                              id="printButtonServicesStats"
                              isOpen={menu}
                              toggle={() => setMenu(!menu)}
                              className="download-btn text-center"
                              type="button"
                            >
                              <DropdownToggle
                                id="downloadstatstable"
                                className="text-muted"
                                tag="i"
                              >
                                <i className="mdi mdi-download" />
                              </DropdownToggle>
                              <DropdownMenu>
                                <DropdownItem onClick={() => generateExcel()}>
                                  {props.t("Download .xlsx")}
                                </DropdownItem>
                                <DropdownItem onClick={() => exportPDF()}>
                                  {props.t("Download .pdf")}
                                </DropdownItem>
                              </DropdownMenu>
                            </Dropdown>
                          </span>
                          <span className="mt-2 float-end">
                            <p>
                              {filteredUsers.length} {props.t("users")}
                            </p>
                          </span>
                        </Col>
                      </Row>

                      <Collapse isOpen={selectedRowKeys.length > 0}>
                        <Row className="pb-2">
                          <Col className="col-auto">
                            <Button
                              color="success"
                              className="compact-button"
                              disabled={selectedRowKeys.length == 0 || loading}
                              onClick={() => {
                                linkAdmins()
                              }}
                            >
                              <i className="mdi mdi-link-variant-plus me-2" />
                              {props.t("Link selected users")}
                            </Button>
                            <Button
                              color="danger"
                              className="ms-3 compact-button"
                              disabled={selectedRowKeys.length == 0 || loading}
                              onClick={() => {
                                unlinkAdmins()
                              }}
                            >
                              <i className="mdi mdi-link-variant-off me-2" />
                              {props.t("Unlink selected users")}
                            </Button>
                          </Col>
                          <Col style={{ alignContent: "center" }}>
                            <span>
                              {selectedRowKeys.length +
                                " " +
                                props.t("selected users")}
                            </span>
                            <Button
                              color="danger"
                              outline
                              size="sm"
                              className="ms-2"
                              onClick={() => {
                                resetSelections()
                              }}
                            >
                              {props.t("Clear selections")}
                            </Button>
                          </Col>
                        </Row>
                      </Collapse>

                      {!loading && (
                        <>
                          <div className="mt-1">
                            <AntdTable
                              className="colored-header narrow-header"
                              bordered
                              size="small"
                              sticky
                              rowKey={"id"}
                              columns={columns}
                              dataSource={filteredUsers}
                              rowSelection={rowSelection}
                              pagination={{
                                showSizeChanger: true,
                                defaultPageSize: 100,
                                pageSizeOptions: defaultPageSizeOptions,
                              }}
                              tableLayout="auto"
                              scroll={{
                                x: "max-content",
                                y: tableDefaultHeight,
                              }}
                            />
                          </div>
                        </>
                      )}

                      {loading && (
                        <Lottie
                          options={getLottieLoadingOptions()}
                          height={100}
                          width={100}
                        ></Lottie>
                      )}
                    </React.Fragment>
                  )}
                </CardBody>
              </Card>
            </div>
          )}

          {/**  User add modal */}
          <Modal
            isOpen={addModal}
            toggle={() => {
              setAddModal(!addModal)
            }}
            centered
          >
            <ModalHeader>{props.t("Create a new user")}</ModalHeader>
            <Form
              className="form-horizontal"
              onSubmit={e => {
                e.preventDefault()
                return false
              }}
            >
              <ModalBody>
                <Alert color={"primary"}>
                  <span>
                    {props.t("User will be linked to currently selected group")}
                  </span>
                </Alert>
                <Alert color={"primary"}>
                  <span>{props.t("UserAllowedChars")}</span>
                </Alert>
                <FormGroup>
                  <Label>{props.t("Username")}</Label>
                  <AntdForm.Item
                    validateStatus={validation.errors.username ? "error" : ""}
                    hasFeedback
                    help={validation.errors.username}
                  >
                    <AntdInput
                      allowClear
                      placeholder={"E.g. firstname.lastname"}
                      value={validation.values.username}
                      onChange={v => {
                        validation.setFieldValue("username", v.target.value)
                      }}
                    />
                  </AntdForm.Item>
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <Button
                  color="primary"
                  outline
                  onClick={() => {
                    setAddModal(false)
                  }}
                >
                  {props.t("Cancel")}
                </Button>
                <Button
                  color="success"
                  disabled={ongoingAdd}
                  onClick={() => {
                    validation.submitForm()
                  }}
                >
                  {props.t("Add")}
                </Button>{" "}
              </ModalFooter>
            </Form>
          </Modal>

          {/**  Group add modal */}
          <Modal
            isOpen={groupAddModal}
            toggle={() => {
              setGroupAddModal(!groupAddModal)
            }}
            centered
          >
            <ModalHeader>{props.t("Create a new group")}</ModalHeader>
            <Form
              className="form-horizontal"
              onSubmit={e => {
                e.preventDefault()
                return false
              }}
            >
              <ModalBody>
                <Alert color={"primary"}>
                  <span>{props.t("GroupAllowedChars")}</span>
                </Alert>
                <FormGroup>
                  <Label>{props.t("Name")}</Label>
                  <AntdForm.Item
                    validateStatus={groupValidation.errors.group ? "error" : ""}
                    hasFeedback
                    help={groupValidation.errors.group}
                  >
                    <AntdInput
                      allowClear
                      placeholder={props.t("Name")}
                      value={groupValidation.values.group}
                      onChange={v => {
                        groupValidation.setFieldValue("group", v.target.value)
                      }}
                    />
                  </AntdForm.Item>
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <Button
                  color="primary"
                  outline
                  onClick={() => {
                    setGroupAddModal(false)
                  }}
                >
                  {props.t("Cancel")}
                </Button>
                <Button
                  color="success"
                  disabled={ongoingAdd}
                  onClick={() => {
                    groupValidation.submitForm()
                  }}
                >
                  {props.t("Add")}
                </Button>{" "}
              </ModalFooter>
            </Form>
          </Modal>

          {/**  Group delete modal */}
          <Modal
            isOpen={groupDeleteModal}
            toggle={() => {
              setGroupDeleteModal(!groupDeleteModal)
            }}
            centered
          >
            <ModalHeader>{props.t("Delete confirmation")}</ModalHeader>
            <ModalBody>
              <p>
                {props.t("Are you sure you want to delete") +
                  ": " +
                  group +
                  "?"}
              </p>
              <Alert color={"primary"}>
                <span>
                  {props.t(
                    "Deletion will fail if the group has any users or devices attached to it!"
                  )}
                </span>
              </Alert>
            </ModalBody>
            <ModalFooter>
              <Button
                color="primary"
                outline
                onClick={() => {
                  setGroupDeleteModal(false)
                }}
              >
                {props.t("Cancel")}
              </Button>
              <Button
                disabled={ongoingAdd}
                color="danger"
                onClick={() => {
                  groupDelete()
                }}
              >
                {props.t("Delete")}
              </Button>
            </ModalFooter>
          </Modal>

          <KeycloakUserDrawer
            showDrawer={showDrawer}
            setShowDrawer={setShowDrawer}
            selectedRow={selectedRow}
            getAdmins={getAdmins}
          />
        </Container>
      </div>
    </>
  )
}

KeycloakUsers.propTypes = {
  t: PropTypes.any,
}

export default withTranslation()(KeycloakUsers)
